import { GDFFrontType, GDFHotPollyTypes, GDFRecordType } from '../../../gdf/type';
import { WeatherJetStreamWidth } from '../../../settings/settings';
import { StyleFactory, WeatherLayerSpecification } from '../styling';
import { getBarbColorExpression, getBarbsExpression } from './common';

/* 

wmohzdvol
wmostorm
wmocat
wmojetstream
wmotrop
hotpolyhi: sig_tropopause
hotpolyhi: sig_icing

HYD:
front
hotpolyhi: tropical_cyclone
hotpolyhi: turbulence_clearair_adv
wmovolcano

*/
const newSigStyles = new Set([
    0x8100, 0x8101, 0x8102, 0x8103, 0x8172, 0x8104, 0x8105, 0x8106, 0x8107, 0x8173, 0x8108, 0x8109, 0x810a, 0x810b, 0x8174, 0x810c, 0x810d,
    0x810e, 0x810f, 0x8175, 0x8608, 0x8609, 0x860a, 0x860b, 0x8600, 0x8601, 0x8602, 0x8603, 0x860c, 0x860d, 0x860e, 0x860f, 0x8604, 0x8605,
    0x8606, 0x8607, 0x8610, 0x8611, 0x8612, 0x8613, 0x8614, 0x8615, 0x8616, 0x8617, 0x8403, 0x8404, 0x8405, 0x8406, 0x8407, 0x8408, 0x8409,
    0x840a
]);

export const sigWxLayers: StyleFactory = (baseId, settings, prodId, isLight) => {
    const labelsMinZoom = settings.sigWx.labelsMinZoomLevel;
    const isNewStyle = !newSigStyles.has(prodId);

    return [
        // tropopause
        {
            type: 'line',
            id: baseId + '-trop-line',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.sig_tropopause],
            source: baseId,
            paint: {
                'line-width': 2,
                'line-dasharray': [2, 1],
                'line-color': '#0B00FF'
            }
        },
        {
            minzoom: labelsMinZoom,
            type: 'symbol',
            id: baseId + '-trop-line-label',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.sig_tropopause],
            source: baseId,
            layout: {
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-field': ['get', 'id'],
                'symbol-placement': 'line',
                'text-anchor': 'bottom'
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        // Hot poly turbulence, appears in DTN sig charts
        {
            type: 'fill',
            id: baseId + '-turbulence-hotpoly',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.turbulence_clearair_adv],
            source: baseId,
            paint: {
                'fill-color': '#32CD32',
                'fill-opacity': ['global-state', 'polygon-transparency']
            }
        },
        {
            type: 'line',
            id: baseId + '-turbulence-hotpoly-line',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.turbulence_clearair_adv],
            source: baseId,
            paint: {
                'line-width': 3,
                'line-color': '#32CD32',
                'line-dasharray': [2, 1]
            }
        },
        // Turbulence, appears in CAE sig charts
        isNewStyle && {
            type: 'fill',
            id: baseId + '-cat',
            hover: true,
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            paint: {
                'fill-color': [
                    'case',
                    ['==', ['get', 'color'], 0],
                    '#d6f400',
                    ['==', ['get', 'color'], 1],
                    '#ff9900',
                    ['==', ['get', 'color'], 2],
                    '#cc0000',
                    ['==', ['get', 'color'], 3],
                    '#7a0000',
                    '#7a0000'
                ],
                'fill-opacity': ['global-state', 'polygon-transparency']
            }
        },
        isNewStyle && {
            type: 'line',
            id: baseId + '-cat-outline',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            paint: {
                'line-width': 2,
                'line-color': [
                    'case',
                    ['==', ['get', 'color'], 0],
                    '#d6f400',
                    ['==', ['get', 'color'], 1],
                    '#ff8d00',
                    ['==', ['get', 'color'], 2],
                    '#f30000',
                    ['==', ['get', 'color'], 3],
                    '#7a0000',
                    '#7a0000'
                ]
            }
        },
        // CAT old style
        !isNewStyle && {
            type: 'fill',
            id: baseId + '-cat-fill',
            hover: true,
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            paint: {
                'fill-color': 'transparent'
            }
        },
        !isNewStyle && {
            type: 'line',
            id: baseId + '-cat',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            paint: {
                'line-color': '#32CD32',
                'line-width': 2,
                'line-dasharray': [2, 1]
            }
        },
        !isNewStyle && {
            minzoom: 4,
            type: 'symbol',
            id: baseId + '-cat-name',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'symbol-placement': 'point',
                'text-anchor': 'bottom',
                'text-offset': [0, 0]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        // Icing, only appears in new sig charts
        {
            hover: true,
            type: 'fill',
            id: baseId + '-icing',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.sig_icing],
            source: baseId,
            paint: {
                'fill-color': 'rgba(0, 123, 224)',
                'fill-opacity': ['global-state', 'polygon-transparency']
            }
        },
        {
            type: 'line',
            id: baseId + '-icing-outline',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.sig_icing],
            source: baseId,
            paint: {
                'line-width': 2,
                'line-color': 'rgba(0, 123, 224)'
            }
        },
        {
            minzoom: labelsMinZoom,
            type: 'symbol',
            id: baseId + '-cat-name',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'symbol-placement': 'point',
                'text-anchor': 'bottom',
                'text-offset': [0, 0]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        {
            type: 'line',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.front], ['==', ['geometry-type'], 'LineString']],
            id: baseId + '-front-lines',
            source: baseId,
            paint: {
                'line-width': [
                    'case',
                    ['==', ['get', 'front'], GDFFrontType.stationary], 48,
                    ['==', ['get', 'front'], GDFFrontType.stationaryCW], 48,
                    ['==', ['get', 'front'], GDFFrontType.stationaryWC], 48,
                    32
                ],
                // prettier-ignore
                'line-pattern': ['image', [
                    'case',
                    ['==', ['get', 'front'], GDFFrontType.cold], 'front-cold',
                    ['==', ['get', 'front'], GDFFrontType.stationaryCC], 'front-cold',
                    ['==', ['get', 'front'], GDFFrontType.warm], 'front-warm',
                    ['==', ['get', 'front'], GDFFrontType.stationaryWW], 'front-warm',
                    ['==', ['get', 'front'], GDFFrontType.stationary], 'front-stationary',
                    ['==', ['get', 'front'], GDFFrontType.occluded], 'front-occluded',
                    ['==', ['get', 'front'], GDFFrontType.tropicalWave], 'front-tropical-wave',
                    ['==', ['get', 'front'], GDFFrontType.surfaceTrough], 'front-surface-through',
                    ['==', ['get', 'front'], GDFFrontType.squallLine], 'front-squall',
                    ['==', ['get', 'front'], GDFFrontType.dryline], 'front-dry-line',
                    ['==', ['get', 'front'], GDFFrontType.stationaryCW], 'front-cold-warm',
                    ['==', ['get', 'front'], GDFFrontType.stationaryWC], 'front-warm-cold',
                    ''
                ]]
            }
        },
        {
            type: 'symbol',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.front], ['==', ['geometry-type'], 'Point']],
            id: baseId + '-front-points',
            source: baseId,
            layout: {
                'text-field': [
                    'format',
                    ['case', ['==', ['get', 'front'], GDFFrontType.highCenter], 'H', 'L'],
                    '\n',
                    ['get', 'reading'],
                    { 'font-scale': 0.5 }
                ],
                'text-allow-overlap': true,
                'text-font': ['Metropolis Bold'],
                'text-size': 25
            },
            paint: {
                'text-color': ['case', ['==', ['get', 'front'], GDFFrontType.highCenter], 'rgb(0,0,127)', 'rgb(192,0,0)']
            }
        },
        {
            type: 'symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmovolcano],
            id: baseId + '-volc2',
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:volcano-filled'],
                'icon-allow-overlap': true
            },
            paint: {
                'icon-color': ['global-state', 'text-color']
            }
        },
        {
            minzoom: labelsMinZoom,
            type: 'symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmovolcano],
            id: baseId + '-volc-name2',
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-anchor': 'top',
                'text-offset': [0, 1.8],
                'icon-image': ['image', 'rect-outline'],
                'icon-text-fit': 'both',
                'icon-allow-overlap': true,
                'icon-text-fit-padding': [1, 1, 1, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        {
            type: 'symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmohzdvol],
            id: baseId + '-volc',
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:volcano-filled'],
                'icon-allow-overlap': true
            },
            paint: {
                'icon-color': ['global-state', 'text-color']
            }
        },
        {
            minzoom: labelsMinZoom,
            type: 'symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmohzdvol],
            id: baseId + '-volc-name',
            themed: true,
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-anchor': 'top',
                'text-offset': [0, 1.8],
                'icon-image': ['image', isLight ? 'rect-outline' : 'rect-outline-dark'],
                'icon-text-fit': 'both',
                'icon-allow-overlap': true,
                'icon-text-fit-padding': [1, 1, 1, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        {
            type: 'symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmohzdrad],
            id: baseId + '-rad',
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:radiation'],
                'icon-allow-overlap': true,
                'icon-size': 0.7
            },
            paint: {
                'icon-color': 'rgb(255,165,1)'
            }
        },
        {
            type: 'symbol',
            id: baseId + '-storm',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmostorm],
            source: baseId,
            layout: {
                'icon-image': [
                    'case',
                    ['==', ['get', 'stormType'], 2],
                    ['image', 'fe-weather:tropical-storm'],
                    ['any', ['==', ['get', 'stormType'], 4], ['==', ['get', 'stormType'], 3]],
                    ['image', 'fe-weather:tropical-hurricane'],
                    ['==', ['get', 'stormType'], 5],
                    ['image', 'fe-weather:meteo-61549'],
                    ''
                ],
                'icon-allow-overlap': true
            },
            paint: {
                'icon-color': '#c10400'
            }
        },
        {
            // TODO:
            type: 'symbol',
            id: baseId + '-storm-hotpoly',
            filter: ['==', ['get', 'type'], GDFHotPollyTypes.tropical_cyclone],
            source: baseId,
            layout: {
                'icon-image': [
                    'case',
                    ['==', ['get', 'id'], 2],
                    ['image', 'fe-weather:tropical-storm'],
                    ['any', ['==', ['get', 'id'], 4], ['==', ['get', 'id'], 3]],
                    ['image', 'fe-weather:tropical-hurricane'],
                    ''
                ],
                'icon-allow-overlap': true
            },
            paint: {
                'icon-color': '#c10400'
            }
        },
        {
            type: 'symbol',
            id: baseId + '-storm-name',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmostorm],
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-anchor': 'top',
                'text-offset': [0, 2]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        {
            minzoom: labelsMinZoom,
            type: 'symbol',
            id: baseId + '-cat-symbol',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocat],
            source: baseId,
            layout: {
                'icon-image': ['image', ['concat', 'fe-weather:pirep', ['get', 'symbol']]],
                'symbol-placement': 'point',
                'icon-anchor': 'bottom',
                'icon-offset': [2, -16],
                'icon-allow-overlap': true
            },
            paint: {
                'icon-color': ['global-state', 'text-color']
            }
        },
        {
            type: 'fill',
            hover: true,
            id: baseId + '-cloud-shape',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmocloud],
            source: baseId,
            paint: {
                'fill-color': 'rgba(0,0,0,0)'
            }
        },
        {
            themed: true,
            minzoom: labelsMinZoom,
            type: 'symbol',
            id: baseId + '-cloud-label',
            filter: [
                'all',
                ['==', ['get', 'recordType'], GDFRecordType.wmocloud],
                ['==', ['geometry-type'], 'Point'],
                ['!=', ['get', 'name'], '']
            ],
            source: baseId,
            layout: {
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-field': ['get', 'name'],
                'icon-image': ['image', isLight ? 'rect-outline' : 'rect-outline-dark'],
                'icon-text-fit': 'both',
                'icon-allow-overlap': true,
                'text-allow-overlap': true,
                'icon-text-fit-padding': [1, 1, 1, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        // Jet streams
        isNewStyle && {
            type: 'line',
            id: baseId + '-jet-stream',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmojetstream],
            source: baseId,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                // todo use green
                'line-pattern': ['image', 'jet-stream'],
                'line-color': 'rgba(2,128,0,0.8)',
                'line-width': jetStreamToWidth(settings.winds.jetStreamWidth)
            }
        },
        isNewStyle && {
            type: 'symbol',
            id: baseId + '-jet-stream-end-arrow',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.wmojetstream], ['==', ['get', 'arrow'], true]],
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:arrow'],
                'icon-size': jetStreamIconWidth(settings.winds.jetStreamWidth),
                'icon-anchor': 'bottom',
                'icon-rotate': ['get', 'angle'],
                'icon-offset': [0, 1.5],
                'icon-allow-overlap': true,
                'icon-pitch-alignment': 'map'
            },
            paint: {
                'icon-color': 'rgba(2,128,0,0.8)'
            }
        },
        !isNewStyle && {
            type: 'line',
            id: baseId + '-jet-stream',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmojetstream],
            source: baseId,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': 'rgb(107, 162, 206)',
                'line-width': jetStreamToWidth(settings.winds.jetStreamWidth)
            }
        },
        !isNewStyle && {
            type: 'symbol',
            id: baseId + '-jet-stream-arrow',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.wmojetstream], ['==', ['get', 'arrow'], true]],
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:arrow'],
                'icon-size': jetStreamIconWidth(settings.winds.jetStreamWidth),
                'icon-anchor': 'center',
                'icon-rotate': ['get', 'angle'],
                'icon-allow-overlap': true,
                'icon-pitch-alignment': 'map'
            },
            paint: {
                'icon-color': 'rgb(107, 162, 206)'
            }
        },
        !isNewStyle && {
            minzoom: 4,
            type: 'symbol',
            id: baseId + '-jet-stream-barb-labels',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmojetstream],
            source: baseId,
            layout: {
                'text-field': ['get', 'label'],
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-allow-overlap': true,
                'text-rotate': ['+', ['get', 'angle'], 90],
                'text-offset': [0, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        {
            type: 'symbol',
            id: baseId + '-jet-stream-barbs',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.wmojetstream], ['has', 'speed']],
            source: baseId,
            layout: {
                // prettier-ignore
                'icon-image': getBarbsExpression(['get', 'speed']),
                'icon-size': ['global-state', 'wind-barbs-size'],
                'icon-rotate': ['get', 'angle'],
                'icon-allow-overlap': false,
                'icon-anchor': 'bottom',
                'icon-rotation-alignment': 'map'
            },
            paint: {
                'icon-color': getBarbColorExpression(settings, ['get', 'speed'])
            }
        },
        !isNewStyle && {
            minzoom: labelsMinZoom,
            type: 'symbol',
            id: baseId + '-jet-stream-barb-labels',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmojetstream],
            source: baseId,
            layout: {
                'text-field': ['get', 'label'],
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-allow-overlap': true,
                'text-rotate': ['+', ['get', 'angle'], 90],
                'text-offset': [0, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        },
        !isNewStyle && {
            type: 'symbol',
            id: baseId + '-jet-stream-barbs',
            filter: ['all', ['==', ['get', 'recordType'], GDFRecordType.wmojetstream], ['has', 'speed']],
            source: baseId,
            layout: {
                // prettier-ignore
                'icon-image': getBarbsExpression(['get', 'speed']),
                'icon-size': ['global-state', 'wind-barbs-size'],
                'icon-rotate': ['get', 'angle'],
                'icon-allow-overlap': false,
                'icon-anchor': 'bottom',
                'icon-rotation-alignment': 'map'
            },
            paint: {
                'icon-color': getBarbColorExpression(settings, ['get', 'speed'])
            }
        },
        {
            themed: true,
            type: 'symbol',
            id: baseId + '-trop',
            filter: ['==', ['get', 'recordType'], GDFRecordType.wmotrop],
            source: baseId,
            layout: {
                'text-field': ['get', 'name'],
                'text-allow-overlap': true,
                'text-font': ['Open Sans Regular'],
                'text-size': 10,
                'text-anchor': 'center',
                'text-offset': [0, 0],
                'icon-image': ['image', isLight ? 'rect-outline' : 'rect-outline-dark'],
                'icon-text-fit': 'both',
                'icon-allow-overlap': true,
                'icon-text-fit-padding': [1, 1, 1, 1]
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        }
    ].filter(Boolean) as WeatherLayerSpecification[];
};

export const jetStreamLayers: StyleFactory = (baseId, settings) => {
    return [
        {
            type: 'line',
            id: baseId + '-jet-stream',
            source: baseId,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': 'rgb(107, 162, 206)',
                'line-width': jetStreamToWidth(settings.winds.jetStreamWidth)
            }
        },
        {
            type: 'symbol',
            filter: ['==', ['geometry-type'], 'Point'],
            id: baseId + '-jet-stream-arrow',
            source: baseId,
            layout: {
                'icon-image': ['image', 'fe-weather:arrow'],
                'icon-size': jetStreamIconWidth(settings.winds.jetStreamWidth),
                'icon-anchor': 'center',
                'icon-rotate': ['get', 'angle'],
                'icon-allow-overlap': true,
                'icon-pitch-alignment': 'map'
            },
            paint: {
                'icon-color': 'rgb(107, 162, 206)'
            }
        }
    ];
};

function jetStreamToWidth(width: WeatherJetStreamWidth): number {
    switch (width) {
        case WeatherJetStreamWidth.extraThin:
            return 2;
        case WeatherJetStreamWidth.thin:
            return 5;
        case WeatherJetStreamWidth.medium:
            return 8;
        case WeatherJetStreamWidth.wide:
            return 12;
    }
}

function jetStreamIconWidth(width: WeatherJetStreamWidth): number {
    switch (width) {
        case WeatherJetStreamWidth.extraThin:
            return 0.375;
        case WeatherJetStreamWidth.thin:
            return 1;
        case WeatherJetStreamWidth.medium:
            return 1.5;
        case WeatherJetStreamWidth.wide:
            return 3;
    }
}
